/**
 * Auth Context - Olympus Betreuungsverwaltung
 * Globaler Authentifizierungs-Status
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/api/authAPI';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(authAPI.getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Session beim Start validieren
  useEffect(() => {
    const validateSession = async () => {
      if (authAPI.isAuthenticated()) {
        try {
          const userData = await authAPI.getMe();
          setUser(userData);
          // Benachrichtigungen laden (nicht-blockierend)
          authAPI.getUngeleseneCount()
            .then(({ count }) => setUnreadCount(count))
            .catch(() => {});
        } catch (error) {
          console.error('Session validation failed:', error);
          setUser(null);
        }
      }
      setLoading(false);
    };

    validateSession();
  }, []);

  // Benachrichtigungen regelmäßig aktualisieren + Token-Refresh
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      try {
        const { count } = await authAPI.getUngeleseneCount();
        setUnreadCount(count);
      } catch (e) {
        // Ignore
      }
    }, 30000); // Alle 30 Sekunden

    // Token alle 30 Minuten erneuern (verhindert Ablauf bei langen Sessions)
    // Same-Origin-Helper (P0-Fix 26.09.2026): siehe authAPI.js SAME_ORIGIN_BASE
    const refreshInterval = setInterval(async () => {
      try {
        const sameOriginBase = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
        const resp = await fetch(`${sameOriginBase}/api/auth/refresh`, {
          method: 'POST',
          headers: authAPI.getAuthHeaders()
        });
        if (resp.ok) {
          const data = await resp.json();
          authAPI.token = data.access_token;
          localStorage.setItem('olympus_token', data.access_token);
        }
      } catch {
        // Ignore - next interval will retry
      }
    }, 30 * 60 * 1000); // Alle 30 Minuten

    return () => { clearInterval(interval); clearInterval(refreshInterval); };
  }, [user]);

  const login = async (email, passwort) => {
    const result = await authAPI.login(email, passwort);
    // 2FA erforderlich? Zurückgeben ohne User zu setzen
    if (result.requires_2fa) {
      return result;
    }
    setUser(result.benutzer);
    try {
      const { count } = await authAPI.getUngeleseneCount();
      setUnreadCount(count);
    } catch {}
    return result;
  };

  const logout = async () => {
    await authAPI.logout();
    setUser(null);
    setUnreadCount(0);
  };

  const refreshUser = useCallback(async () => {
    if (authAPI.isAuthenticated()) {
      const userData = await authAPI.getMe();
      setUser(userData);
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (user) {
      const { count } = await authAPI.getUngeleseneCount();
      setUnreadCount(count);
    }
  }, [user]);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.rolle === 'admin',
    isBetreuer: user?.rolle === 'betreuer' || user?.rolle === 'admin',
    hasRole: (roles) => {
      if (!user) return false;
      const allowedRoles = Array.isArray(roles) ? roles : [roles];
      return allowedRoles.includes(user.rolle);
    },
    unreadCount,
    refreshNotifications,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
